import { useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

export function InstallBanner() {
  const { isInstalled, isIOS, canInstall, isBannerDismissed, install, dismissBanner } =
    useInstallPrompt();
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Don't render if already installed, dismissed, or on desktop with no install prompt
  if (isInstalled || isBannerDismissed) return null;
  if (!canInstall && !isIOS) return null;

  return (
    <>
      {/* Banner */}
      <div className="relative rounded-xl border border-[#2563EB]/30 bg-gradient-to-r from-[#2563EB]/10 to-[#06B6D4]/10 dark:from-[#2563EB]/20 dark:to-[#06B6D4]/20 p-4">
        <button
          type="button"
          onClick={dismissBanner}
          className="absolute top-3 right-3 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Banner schliessen"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2563EB] to-[#06B6D4]">
            <Smartphone className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              VajraSila installieren
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
              Fuer Push-Erinnerungen &amp; Offline-Modus
            </p>

            {/* Android: native install */}
            {canInstall && (
              <Button
                size="sm"
                className="bg-vajra-blue hover:bg-vajra-blue/90"
                onClick={install}
              >
                <Download className="h-4 w-4 mr-1.5" />
                Installieren
              </Button>
            )}

            {/* iOS: show instructions */}
            {isIOS && !canInstall && (
              <Button
                size="sm"
                variant="outline"
                className="border-[#2563EB]/40 text-vajra-blue dark:text-vajra-cyan"
                onClick={() => setShowIOSModal(true)}
              >
                <Download className="h-4 w-4 mr-1.5" />
                Installieren
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowIOSModal(false)}
        >
          <div
            className="w-full max-w-sm bg-white dark:bg-[#0D1117] rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-[#1E3A5F]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-[#2563EB]/10 dark:bg-[#2563EB]/20 rounded-xl">
                  <Smartphone className="h-5 w-5 text-vajra-blue dark:text-vajra-cyan" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  App installieren
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowIOSModal(false)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Safari hint */}
            <div className="mb-5 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40">
              <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                Nur in <strong>Safari</strong> möglich — Chrome, Firefox &amp;
                Co. unterstuetzen die Installation auf iPhone nicht.
              </p>
            </div>

            {/* Steps */}
            <ol className="space-y-4">
              <li className="flex items-center gap-4">
                <span className="shrink-0 w-8 h-8 rounded-xl bg-[#2563EB]/10 dark:bg-[#2563EB]/20 text-vajra-blue dark:text-vajra-cyan text-sm font-bold flex items-center justify-center">
                  1
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Teilen-Symbol tippen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Das Quadrat mit dem Pfeil nach oben in Safari
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="shrink-0 w-8 h-8 rounded-xl bg-[#2563EB]/10 dark:bg-[#2563EB]/20 text-vajra-blue dark:text-vajra-cyan text-sm font-bold flex items-center justify-center">
                  2
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    &bdquo;Zum Home-Bildschirm&ldquo;
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Im Menu nach unten scrollen
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="shrink-0 w-8 h-8 rounded-xl bg-[#2563EB]/10 dark:bg-[#2563EB]/20 text-vajra-blue dark:text-vajra-cyan text-sm font-bold flex items-center justify-center">
                  3
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    &bdquo;Hinzufuegen&ldquo; tippen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Oben rechts im Dialog bestaetigen
                  </p>
                </div>
              </li>
            </ol>

            <button
              type="button"
              onClick={() => setShowIOSModal(false)}
              className="mt-6 w-full py-3 rounded-xl bg-vajra-blue hover:bg-vajra-blue/90 text-white text-sm font-semibold transition-colors"
            >
              Verstanden
            </button>
          </div>
        </div>
      )}
    </>
  );
}
